/* Notification Service — Push notifications + in-app alerts */
const API = process.env.REACT_APP_BACKEND_URL;

class NotificationService {
  constructor() {
    this.permission = Notification.permission || 'default';
    this.subscription = null;
    this.listeners = new Set();
    this.inAppQueue = [];
  }

  /* ── Register service worker ─────────────────────── */
  async init() {
    // Service worker disabled — was causing stale cache issues on mobile devices
    // Unregister any existing service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) { await reg.unregister(); }
    }
    return false;
  }

  /* ── Request push permission ─────────────────────── */
  async requestPermission() {
    if (!('Notification' in window)) return 'unsupported';
    this.permission = await Notification.requestPermission();
    return this.permission;
  }

  /* ── Subscribe to push ──────────────────────────── */
  async subscribeToPush(userId) {
    if (this.permission !== 'granted') {
      const perm = await this.requestPermission();
      if (perm !== 'granted') return null;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      this.subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this._urlBase64ToUint8Array(
          // VAPID public key — will be configured from backend
          'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkPs-bQKhPPBqHvJ7lGgAY5kCkzr8UjJhAT5mCVzE'
        ),
      });
      // Send subscription to backend
      if (userId) {
        await fetch(`${API}/notifications/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, subscription: this.subscription.toJSON() }),
        }).catch(() => {}); // Silently fail if endpoint doesn't exist yet
      }
      return this.subscription;
    } catch (err) {
      console.warn('Push subscribe failed:', err);
      return null;
    }
  }

  /* ── In-app notification ────────────────────────── */
  notify(notification) {
    // { type: 'price_drop' | 'system' | 'info', title, message, action?, data? }
    this.inAppQueue.push({
      ...notification,
      id: Date.now(),
      timestamp: new Date().toISOString(),
      read: false,
    });
    this.listeners.forEach((fn) => fn([...this.inAppQueue]));
  }

  /* ── Subscribe to in-app notifications ──────────── */
  onNotification(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /* ── Mark as read ───────────────────────────────── */
  markRead(id) {
    const n = this.inAppQueue.find((n) => n.id === id);
    if (n) n.read = true;
    this.listeners.forEach((fn) => fn([...this.inAppQueue]));
  }

  /* ── Get unread count ───────────────────────────── */
  getUnreadCount() {
    return this.inAppQueue.filter((n) => !n.read).length;
  }

  /* ── Check online status ────────────────────────── */
  isOnline() {
    return navigator.onLine;
  }

  /* ── Connection status monitoring ───────────────── */
  onConnectionChange(callback) {
    const online = () => callback(true);
    const offline = () => callback(false);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }

  _urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
    return outputArray;
  }
}

const notificationService = new NotificationService();
export default notificationService;
